/**
 * Standard empty / no-data state. Use anywhere a list or panel
 * comes back with nothing to render — the audit found at least
 * four different ad-hoc empty states (Dashboard, Foundry, Jobs,
 * the workbook tab) with subtly different layouts and copy.
 *
 *   <EmptyState
 *     icon={Database}
 *     title="No studies yet"
 *     body="Create your first study from the catalog to get started."
 *     action={<Button>Browse catalog</Button>}
 *   />
 */
import React from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";


export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  body?: React.ReactNode;
  action?: React.ReactNode;
  /** Use "compact" inside small cards (forecast tabs, sidebar bodies);
   *  default "default" is for full-section empties. */
  size?: "default" | "compact";
  className?: string;
}


export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  size = "default",
  className,
}: EmptyStateProps) {
  const compact = size === "compact";
  return (
    <div
      className={cn(
        "flex flex-col items-center text-center border border-dashed rounded-lg",
        compact ? "gap-2 p-6" : "gap-3 p-10",
        className,
      )}
    >
      {Icon && (
        <div
          className={cn(
            "flex items-center justify-center rounded-full bg-muted/60 text-muted-foreground",
            compact ? "w-8 h-8" : "w-12 h-12",
          )}
        >
          <Icon className={compact ? "w-4 h-4" : "w-5 h-5"} />
        </div>
      )}
      <div className="space-y-1">
        <p
          className={cn(
            "font-medium text-foreground",
            compact ? "text-sm" : "text-base",
          )}
        >
          {title}
        </p>
        {body && (
          <p
            className={cn(
              "text-muted-foreground max-w-md mx-auto",
              compact ? "text-xs" : "text-sm",
            )}
          >
            {body}
          </p>
        )}
      </div>
      {action && <div className={compact ? "mt-1" : "mt-2"}>{action}</div>}
    </div>
  );
}
